/**
 * BrowsePage — /browse
 *
 * Implements WI-32e (FR-059): a searchable, filterable directory of every
 * school in the corpus, including those with no scraped data.
 *
 * Layout:
 *   - AppHeader (shared)
 *   - Page heading + subtitle with total count
 *   - Name-search input (debounced 250 ms) with clear button
 *   - Division filter chips (aria-pressed, immediate API call on toggle)
 *   - Sort toggle (A–Z | Division | Region)
 *   - School list (ul/li rows, each linking to /schools/:slug)
 *   - "Load more" button (appends next page; hidden when all loaded)
 *   - Empty state with clear-search button
 *   - Desktop sidebar ad slot (rectangle)
 *   - aria-live region for result count announcements
 *
 * Accessibility:
 *   - Name-search input: aria-label="Filter schools by name"
 *   - School list: semantic <ul><li> structure
 *   - Division chips: aria-pressed state
 *   - Result count: aria-live="polite"
 *   - WCAG 2.1 AA
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AdSlot } from './AdSlot';
import { useAuth } from '../hooks/useAuth';
import { fetchDirectory, DirectorySchool, DirectoryParams } from '../api/directory';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;

const DIVISION_OPTIONS = [
  { slug: 'ncaa_d1', label: 'D1' },
  { slug: 'ncaa_d2', label: 'D2' },
  { slug: 'ncaa_d3', label: 'D3' },
  { slug: 'naia', label: 'NAIA' },
  { slug: 'njcaa', label: 'NJCAA' },
] as const;

type SortOption = 'name' | 'division' | 'region';

// ── Sub-components ────────────────────────────────────────────────────────────

/** Division badge displayed on each school row. */
function DivisionBadge({ division }: { division: string }) {
  const label =
    division === 'ncaa_d1' ? 'D1' :
    division === 'ncaa_d2' ? 'D2' :
    division === 'ncaa_d3' ? 'D3' :
    division === 'naia'    ? 'NAIA' :
    division === 'njcaa'   ? 'NJCAA' :
    division.toUpperCase();
  return (
    <span className="inline-block px-1.5 py-px text-[10px] font-bold tracking-wide bg-surface-sunken text-gray-700 rounded flex-shrink-0">
      {label}
    </span>
  );
}

/** Subtle "No scoring data yet" marker for schools without data. */
function NoDataMarker() {
  return (
    <span
      className="text-[10px] text-gray-400 font-medium ml-1 flex-shrink-0"
      aria-label="No scoring data yet"
    >
      No scoring data yet
    </span>
  );
}

// ── BrowsePage ────────────────────────────────────────────────────────────────

export function BrowsePage() {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const resolvedAuthenticated = authLoading ? false : isAuthenticated;

  // ── Filter state ─────────────────────────────────────────────────────────

  const [nameQuery, setNameQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [sort, setSort] = useState<SortOption>('name');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Result state ─────────────────────────────────────────────────────────

  const [schools, setSchools] = useState<DirectorySchool[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Whether all pages have been fetched.
  const hasMore = currentPage * PAGE_SIZE < total;

  // ── Debounce name query ──────────────────────────────────────────────────

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(nameQuery);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [nameQuery]);

  // ── Initial / filter-change fetch ────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentPage(1);

    const fetchParams: DirectoryParams = {
      q: debouncedQuery || undefined,
      division: selectedDivisions.length > 0 ? selectedDivisions : undefined,
      sort,
      page: 1,
      page_size: PAGE_SIZE,
    };

    fetchDirectory(fetchParams)
      .then((data) => {
        if (cancelled) return;
        setSchools(data.schools);
        setTotal(data.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load schools');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, selectedDivisions, sort]);

  // ── Load more ────────────────────────────────────────────────────────────

  function handleLoadMore() {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    const fetchParams: DirectoryParams = {
      q: debouncedQuery || undefined,
      division: selectedDivisions.length > 0 ? selectedDivisions : undefined,
      sort,
      page: nextPage,
      page_size: PAGE_SIZE,
    };
    fetchDirectory(fetchParams)
      .then((data) => {
        setSchools((prev) => [...prev, ...data.schools]);
        setTotal(data.total);
        setCurrentPage(nextPage);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }

  // ── Division chip toggle ─────────────────────────────────────────────────

  function toggleDivision(slug: string) {
    setSelectedDivisions((prev) =>
      prev.includes(slug) ? prev.filter((d) => d !== slug) : [...prev, slug],
    );
  }

  // ── Clear search ─────────────────────────────────────────────────────────

  function handleClearSearch() {
    setNameQuery('');
    setDebouncedQuery('');
    setSelectedDivisions([]);
  }

  // ── Sort label helper ────────────────────────────────────────────────────

  const sortLabels: Record<SortOption, string> = {
    name: 'A–Z',
    division: 'Division',
    region: 'Region',
  };

  // ── Live count announcement text ─────────────────────────────────────────

  const countAnnouncement = loading
    ? 'Loading schools…'
    : `${total.toLocaleString()} school${total !== 1 ? 's' : ''} found`;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-surface-base">
      <AppHeader isAuthenticated={resolvedAuthenticated} />

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="md:flex gap-6 items-start">

          {/* ── Main content ─────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* Page heading */}
            <div className="mb-5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                All Schools
              </h1>
              {!loading && (
                <p className="text-sm text-gray-500 mt-1">
                  {total.toLocaleString()} schools across NCAA D1–D3, NAIA, and NJCAA.
                </p>
              )}
            </div>

            {/* Name-search input */}
            <div className="relative mb-3">
              <label htmlFor="browse-search" className="sr-only">
                Filter schools by name
              </label>
              <input
                id="browse-search"
                type="text"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Search schools by name…"
                aria-label="Filter schools by name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
              {nameQuery && (
                <button
                  type="button"
                  onClick={() => { setNameQuery(''); setDebouncedQuery(''); }}
                  aria-label="Clear name filter"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Division filter chips */}
            <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Filter by division">
              {DIVISION_OPTIONS.map((opt) => {
                const isSelected = selectedDivisions.includes(opt.slug);
                return (
                  <button
                    key={opt.slug}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => toggleDivision(opt.slug)}
                    className={`px-2.5 py-1 text-xs rounded-full border font-medium motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1 ${
                      isSelected
                        ? 'bg-brand-primary border-brand-primary text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-brand-primary'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Sort toggle + result count */}
            <div className="flex items-center justify-between mb-3">
              {/* aria-live region for count announcements */}
              <p
                aria-live="polite"
                aria-atomic="true"
                className="text-sm text-gray-500 tabular-nums"
              >
                {countAnnouncement}
              </p>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-gray-500 mr-0.5 text-xs">Sort:</span>
                {(['name', 'division', 'region'] as SortOption[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSort(s)}
                    aria-pressed={sort === s}
                    className={`px-2 py-0.5 min-h-[44px] rounded text-xs motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${
                      sort === s
                        ? 'border border-brand-primary text-brand-primary font-semibold'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {sortLabels[s]}
                  </button>
                ))}
              </div>
            </div>

            {/* Error state */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Loading skeleton */}
            {loading && (
              <div className="divide-y divide-hairline border border-hairline rounded-lg overflow-hidden">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-14 bg-white animate-pulse" />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && total === 0 && (
              <div className="text-center py-12">
                <p className="text-base font-semibold text-gray-700 mb-2">
                  No schools match &ldquo;{nameQuery || selectedDivisions.join(', ')}&rdquo;.
                </p>
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="text-brand-primary hover:underline text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                >
                  Clear search
                </button>
              </div>
            )}

            {/* School list */}
            {!loading && !error && schools.length > 0 && (
              <ul
                className="divide-y divide-hairline border border-hairline rounded-lg overflow-hidden"
                aria-label={`${total.toLocaleString()} school results`}
              >
                {schools.map((school) => (
                  <li key={school.slug} className="bg-white">
                    <Link
                      to={`/schools/${school.slug}`}
                      className="flex items-center gap-3 px-4 py-3.5 hover:bg-surface-sunken motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[15px] font-semibold text-gray-900 leading-snug truncate hover:text-brand-primary">
                            {school.name}
                          </span>
                          {!school.has_data && <NoDataMarker />}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500 flex-wrap">
                          <DivisionBadge division={school.division} />
                          <span>{school.region}</span>
                          <span aria-hidden="true">·</span>
                          <span>{school.state}</span>
                          {/* City shown on desktop */}
                          <span className="hidden md:inline">
                            <span aria-hidden="true">·</span>
                            {' '}{school.city}
                          </span>
                        </div>
                      </div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="flex-shrink-0 text-gray-400"
                      >
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Load more button */}
            {!loading && !error && hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-brand-primary hover:text-brand-primary motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>

          {/* ── Desktop ad sidebar ────────────────────────────────────── */}
          <div className="hidden md:block w-56 flex-shrink-0">
            <div className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.12em] mb-2">
              SPONSORED
            </div>
            <AdSlot variant="rectangle" slotId={process.env.REACT_APP_AD_SLOT_BROWSE} />
          </div>

        </div>
      </div>
    </div>
  );
}
